/**
 * Shared outbound messaging utilities (WhatsApp wa.me / api.whatsapp.com links,
 * phone formatting, channel defaults).
 */

export const DEFAULT_OUTBOUND_CHANNEL_STORAGE_KEY =
  "bubakii.outbound.defaultChannelId";
export const OPS_OUTBOUND_CHANNEL_STORAGE_KEY =
  "bubakii.outbound.opsChannelId";
export const PARTNER_OUTBOUND_CHANNEL_STORAGE_KEY =
  "bubakii.outbound.partnerChannelId";

/**
 * Normalize raw digits for South African WhatsApp deep links.
 * Handles trunk-0-after-country (`+27 0…`), international `00` prefix, and
 * local `0…` / bare-9-digit forms ops commonly paste.
 *
 * @param {string} phone
 * @returns {string|null} Digits only with SA country code (e.g. 27825551234)
 */
export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;

  let digitsOnly = String(phone).replace(/\D/g, "");

  // International access prefix (00…) → drop before country code
  if (digitsOnly.startsWith("00")) {
    digitsOnly = digitsOnly.slice(2);
  }

  // SA edge case: country code + trunk 0 (e.g. +27 082 555 1234 → 270825551234)
  if (digitsOnly.startsWith("270") && digitsOnly.length === 12) {
    digitsOnly = `27${digitsOnly.slice(3)}`;
  }

  if (digitsOnly.length > 12) {
    console.warn(
      `Invalid phone number format (too long, likely wa_id): ${phone}`
    );
    return null;
  }

  if (digitsOnly.length < 9) {
    console.warn(`Invalid phone number format (too short): ${phone}`);
    return null;
  }

  if (digitsOnly.startsWith("27")) {
    if (digitsOnly.length === 11) {
      return digitsOnly;
    }
    console.warn(
      `Invalid phone number format (27 prefix but wrong length): ${phone}`
    );
    return null;
  }

  if (digitsOnly.startsWith("0")) {
    if (digitsOnly.length === 10) {
      return `27${digitsOnly.substring(1)}`;
    }
    console.warn(
      `Invalid phone number format (0 prefix but wrong length): ${phone}`
    );
    return null;
  }

  // Bare local mobile (9 digits) or similar without leading 0
  if (digitsOnly.length === 9) {
    return `27${digitsOnly}`;
  }

  console.warn(`Invalid phone number format (unexpected): ${phone}`);
  return null;
};

/**
 * @param {Object} raw
 * @returns {{ id: string, label: string, phone: string, type: string, subtitle?: string } | null}
 */
export const normalizeChannel = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const phone = typeof raw.phone === "string" ? raw.phone.trim() : raw.phone;
  if (!phone) return null;

  return {
    id: String(raw.id || phone),
    label: raw.label || raw.name || phone,
    phone,
    type: raw.type || "whatsapp",
    subtitle: raw.subtitle,
  };
};

/**
 * @param {string} storageKey
 * @returns {string|null}
 */
export const getDefaultChannelId = (storageKey = DEFAULT_OUTBOUND_CHANNEL_STORAGE_KEY) => {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

/**
 * @param {string} storageKey
 * @param {string} channelId
 */
export const setDefaultChannelId = (
  storageKey = DEFAULT_OUTBOUND_CHANNEL_STORAGE_KEY,
  channelId
) => {
  if (!channelId) return;
  try {
    localStorage.setItem(storageKey, channelId);
  } catch {
    // Ignore quota / private mode errors
  }
};

/**
 * Build a wa.me deep link (preferred for ops / partner share).
 * @param {string} recipientPhone
 * @param {string} [messageText]
 * @returns {string|null}
 */
export const buildWhatsAppUrl = (recipientPhone, messageText = "") => {
  const formattedPhone = formatPhoneForWhatsApp(recipientPhone);
  if (!formattedPhone) return null;

  const params = new URLSearchParams();
  if (messageText) {
    params.set("text", messageText);
  }

  const query = params.toString();
  return query
    ? `https://wa.me/${formattedPhone}?${query}`
    : `https://wa.me/${formattedPhone}`;
};

/**
 * Build an api.whatsapp.com/send link (used by PWA / older surfaces).
 * @param {string} recipientPhone
 * @param {string} [messageText]
 * @returns {string|null}
 */
export const buildWhatsAppApiUrl = (recipientPhone, messageText = "") => {
  const formattedPhone = formatPhoneForWhatsApp(recipientPhone);
  if (!formattedPhone) return null;

  const base = `https://api.whatsapp.com/send/?phone=${formattedPhone}`;
  return messageText
    ? `${base}&text=${encodeURIComponent(messageText)}`
    : base;
};

/**
 * @param {string} url
 */
export const openWhatsAppUrl = (url) => {
  if (!url) {
    throw new Error("Cannot open WhatsApp: invalid link");
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Open WhatsApp for an outbound message.
 * @param {'fixed'|'channel'} recipientMode
 * @param {string} [recipientPhone] - Customer phone when recipientMode is fixed
 * @param {{ phone: string }} channel - Selected channel
 * @param {string} messageText
 */
export const openOutboundWhatsApp = ({
  recipientMode = "fixed",
  recipientPhone,
  channel,
  messageText = "",
}) => {
  const targetPhone =
    recipientMode === "channel" ? channel?.phone : recipientPhone;

  if (!targetPhone) {
    throw new Error("Recipient phone number is required");
  }

  const url = buildWhatsAppUrl(targetPhone, messageText);
  if (!url) {
    throw new Error("Cannot generate WhatsApp link: invalid phone number");
  }

  openWhatsAppUrl(url);
};
